import { PAYZHE_BTP_WS_BASE } from './config';
import { getOrCreateZeettuClientId } from './bluetoothProviderSettings';
import { getStoredPayzheBtpToken } from './nativeBtpClient';
import type { PayzheBtpApiMessage } from './types';

/** Topics required by the Kotlin WebSocket server (`CommandDispatcher`). */
export const PAYZHE_BTP_WS_TOPICS = ['printer', 'queue', 'service'] as const;

export function subscribePayzheBtpWebSocketTopics(): boolean {
  const connected =
    sendPayzheBtpWsCommand('connect', { clientId: getOrCreateZeettuClientId() }) &&
    sendPayzheBtpWsCommand('subscribe', { topics: [...PAYZHE_BTP_WS_TOPICS] });
  return connected;
}

export type PayzheBtpWebSocketHandlers = {
  onMessage: (message: PayzheBtpApiMessage) => void;
  onOpen?: () => void;
  onClose?: () => void;
  onError?: () => void;
};

let activeSocket: WebSocket | null = null;

export function getActivePayzheBtpWebSocket(): WebSocket | null {
  if (!activeSocket || activeSocket.readyState !== WebSocket.OPEN) {
    return null;
  }
  return activeSocket;
}

export function sendPayzheBtpWsCommand(
  type: string,
  payload: Record<string, unknown> = {},
): boolean {
  const ws = getActivePayzheBtpWebSocket();
  if (!ws) return false;
  const message = {
    id: `zeettu-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    type,
    timestamp: new Date().toISOString(),
    payload,
  };
  try {
    ws.send(JSON.stringify(message));
    return true;
  } catch {
    return false;
  }
}

export function connectPayzheBtpWebSocket(
  handlers: PayzheBtpWebSocketHandlers,
  token?: string,
): () => void {
  const authToken = token ?? getStoredPayzheBtpToken();
  const url = new URL(PAYZHE_BTP_WS_BASE);
  if (authToken) {
    url.searchParams.set('token', authToken);
  }

  let ws: WebSocket | null = null;
  try {
    ws = new WebSocket(url.toString());
    activeSocket = ws;
  } catch {
    handlers.onError?.();
    return () => undefined;
  }

  ws.onopen = () => {
    subscribePayzheBtpWebSocketTopics();
    handlers.onOpen?.();
  };
  ws.onclose = () => {
    if (activeSocket === ws) {
      activeSocket = null;
    }
    handlers.onClose?.();
  };
  ws.onerror = () => handlers.onError?.();
  ws.onmessage = (event) => {
    try {
      const parsed = JSON.parse(String(event.data)) as PayzheBtpApiMessage;
      if (parsed && typeof parsed.type === 'string') {
        handlers.onMessage(parsed);
      }
    } catch {
      handlers.onMessage({
        id: `${Date.now()}`,
        type: 'raw',
        timestamp: new Date().toISOString(),
        payload: { raw: String(event.data) },
      });
    }
  };

  return () => {
    try {
      ws?.close();
    } catch {
      /* ignore */
    }
    if (activeSocket === ws) {
      activeSocket = null;
    }
    ws = null;
  };
}
